import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ProposalFreeLayout.module.css';
import ProposalRichText from '../ProposalRichText/ProposalRichText';
import ProposalTitle from '../ProposalTitle/ProposalTitle';
import ProposalImage from '../ProposalImage/ProposalImage';
import EditableDataTable from '../../web/EditableDataTable/EditableDataTable';
import ProposalCheckbox from '../ProposalCheckbox/ProposalCheckbox';
import ProposalSelector from '../ProposalSelector/ProposalSelector';
import { buildShapeStyle } from '../shapeStyleUtils';

const BLOCK_COMPONENTS = {
  ProposalRichText,
  ProposalTitle,
  ProposalImage,
  EditableDataTable,
  ProposalCheckbox,
  ProposalSelector,
};

function renderBlock(block, itemId) {
  if (!block?.type) return null;
  const Component = BLOCK_COMPONENTS[block.type];
  if (!Component) return null;
  return <Component key={itemId} {...block.props} />;
}

function renderShape(item) {
  return (
    <div
      className={styles.shape}
      style={{
        ...buildShapeStyle({
          shape: item.shape,
          color: item.color,
          borderColor: item.borderColor,
          borderWidth: item.borderWidth,
          rotation: item.rotation,
          width: item.width,
          height: item.height,
        }),
      }}
    />
  );
}

const ProposalFreeLayout = ({
  id,
  canvasWidth = 825,
  minHeight = 240,
  marginBottom = '',
  items = [],
}) => {
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(canvasWidth);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;

    const update = () => setViewportWidth(node.clientWidth || canvasWidth);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => update());
      ro.observe(node);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [canvasWidth]);

  const scale = useMemo(() => {
    if (!canvasWidth) return 1;
    const ratio = viewportWidth / canvasWidth;
    return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  }, [viewportWidth, canvasWidth]);

  const viewportHeight = Math.max(120, Math.ceil(minHeight * scale));
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <section
      id={id}
      className={styles.section}
      style={{ marginBottom: marginBottom || undefined }}
    >
      <div
        ref={viewportRef}
        className={styles.viewport}
        style={{ height: `${viewportHeight}px` }}
      >
        <div
          className={styles.canvas}
          style={{
            width: `${canvasWidth}px`,
            minHeight: `${minHeight}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {items.map((item) => (
            <div
              key={item.id}
              className={styles.item}
              style={{
                left: `${Number(item.left || 0)}px`,
                top: `${Number(item.top || 0)}px`,
                width: item.width != null ? `${Number(item.width)}px` : undefined,
                height: item.height != null ? `${Number(item.height)}px` : undefined,
                zIndex: Number(item.zIndex || 0),
                overflow: item.kind === 'shape' ? 'hidden' : 'visible',
              }}
            >
              {item.kind === 'shape' ? renderShape(item) : renderBlock(item.block, item.id)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProposalFreeLayout;
